import { useTheme } from "@mui/material";
import { Box } from "lucide-react";
import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import type { PaidAdvisoryDetailDataDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import {
	BasicInfo,
	hasBasicInfo,
} from "web_myplann/Portfolio/Components/PortfolioSections/BasicInfo";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import { computeRelatedPresence } from "web_myplann/Portfolio/Helpers/getHasRelated";
import { getPortfolioFrequencyValue } from "web_myplann/Portfolio/Helpers/portfolioDetailFormatters";
import { DataCardPreset } from "web_myplann/Shared/Components/DataCard";
import {
	DataRowCard,
	type DataRowCardItem,
} from "web_myplann/Shared/Components/DataRowCard";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

const PageResources = Resources.PortfolioDetail.Services;

export const PaidAdvisory: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
}) => {
	const { t } = useResource();
	const theme = useTheme();

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);

	if (isDocumentsLoading) {
		return <DetailSectionLoader />;
	}

	const portfolioData = baseData.data as PaidAdvisoryDetailDataDTO;

	const fixedPrice = portfolioData.fixedPriceByFrequency
		? getPortfolioFrequencyValue(
				portfolioData?.fixedPriceByFrequency ?? 0,
				currencySign,
				portfolioData?.paymentFrequency ?? null,
				t,
			)
		: EMPTY_DETAIL_FIELD;

	const basicInfoItems: DataRowCardItem[] = [
		{ label: t(PageResources.name), value: baseData.clientName },
		{
			label: t(PageResources.contractNumber),
			value: baseData?.contractNumber,
		},
		{ label: t(PageResources.producent), value: baseData?.producer },
		{ label: t(PageResources.product), value: baseData?.product },
	];

	const priceItems: DataRowCardItem[] = [
		{
			label: t(Resources.PortfolioDetail.PaidAdvisory.fixedPrice),
			value: fixedPrice,
		},
	];

	return (
		<>
			<DataRowCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={
					hasBasicInfo(baseData) ? <BasicInfo data={baseData} /> : null
				}
				items={basicInfoItems}
			/>

			<DataRowCard
				label={t(PageResources.cardLabel)}
				icon={<Box color={theme.palette.deprecated.grey[700]} />}
				noDivider={
					!computeRelatedPresence(documents?.data, baseData.related).hasAny
				}
				items={priceItems}
			/>

			<RelatedSection
				documents={documents?.data}
				related={baseData.related}
				portfolioId={baseData.id}
			/>
		</>
	);
};
